import type Phaser from 'phaser';
import type { Simulation } from '../../sim/simulation';
import { TILE_SIZE } from '../layout';

/** Light pool texture: rings of falling brightness (stepped, to stay pixel art). */
const LIGHT_KEY = 'lightPool';
const LIGHT_SIZE = 96;
/** Ring radii (of LIGHT_SIZE / 2) and how much darkness each takes away. */
const RINGS: readonly [number, number][] = [
  [48, 0.25],
  [42, 0.5],
  [35, 0.75],
  [27, 1],
];
/** The night: nearly black, with a little moonlight to make out shapes. */
const NIGHT = 0x05071a;
const NIGHT_ALPHA = 0.93;
const MARGIN = TILE_SIZE * 4;
/** Light pools: the mower's headlamp, and a lit lantern (scales of the light texture). */
const HEADLAMP = 0.8;
const LANTERN = 1.1;
/** An unlit lantern's faint ember, so it can be found in the dark. */
const EMBER = 0.22;

function createLightPool(scene: Phaser.Scene): void {
  if (scene.textures.exists(LIGHT_KEY)) return;
  const tex = scene.textures.createCanvas(LIGHT_KEY, LIGHT_SIZE, LIGHT_SIZE);
  if (!tex) return;
  const c = LIGHT_SIZE / 2;
  for (const [r, a] of RINGS) {
    tex.context.fillStyle = `rgba(255,255,255,${a})`;
    for (let y = 0; y < LIGHT_SIZE; y++) {
      for (let x = 0; x < LIGHT_SIZE; x++) {
        const d = Math.hypot(x + 0.5 - c, y + 0.5 - c);
        if (d <= r) tex.context.fillRect(x, y, 1, 1);
      }
    }
  }
  tex.refresh();
}

/**
 * Boss: Lights Out (lm-pmd). The lawn under night: darkness over everything but the pool of the
 * mower's headlamp and the old lanterns, each lit for good once the mower passes beside it. The
 * sim plays a normal lawn; this only draws.
 */
export class Darkness {
  private readonly rt: Phaser.GameObjects.RenderTexture;
  private readonly lanterns: { x: number; y: number; lit: boolean }[];
  private readonly glows: Phaser.GameObjects.Graphics;
  /** Light pools, kept off the display list: drawn into the night in erase mode. */
  private readonly lights: Phaser.GameObjects.Image[];
  private readonly headlamp: Phaser.GameObjects.Image;

  constructor(
    private readonly scene: Phaser.Scene,
    private sim: Simulation,
    art: readonly string[],
    depth: number,
  ) {
    createLightPool(scene);
    const { width, height } = sim.grid;
    this.lanterns = [];
    art.forEach((id, i) => {
      if (id === 'lantern')
        this.lanterns.push({ x: i % width, y: Math.floor(i / width), lit: false });
    });
    const w = width * TILE_SIZE + MARGIN * 2;
    const h = height * TILE_SIZE + MARGIN * 2;
    this.rt = scene.add.renderTexture(-MARGIN, -MARGIN, w, h).setOrigin(0).setDepth(depth);
    const light = () =>
      scene.make.image({ key: LIGHT_KEY } as Phaser.Types.GameObjects.Sprite.SpriteConfig, false);
    this.lights = this.lanterns.map(() => light());
    this.headlamp = light().setScale(HEADLAMP);
    // Warm light round lit lanterns, over the dark.
    this.glows = scene.add.graphics().setDepth(depth + 1);
  }

  /** Reads a new copy of the lawn from now on (head to head rebuilds it on a late turn). */
  resync(sim: Simulation): void {
    this.sim = sim;
  }

  /** Lanterns lit so far, and how many there are. */
  get lit(): { lit: number; total: number } {
    return { lit: this.lanterns.filter((l) => l.lit).length, total: this.lanterns.length };
  }

  /** Lights any lantern beside the mower, then redraws the night around the lights. */
  render(px: number, py: number, now: number): void {
    const m = this.sim.mower;
    let changed = false;
    for (const l of this.lanterns) {
      if (l.lit || Math.max(Math.abs(l.x - m.x), Math.abs(l.y - m.y)) > 1) continue;
      l.lit = true;
      changed = true;
      this.scene.events.emit('lanternLit', l.x, l.y);
    }
    const rt = this.rt;
    rt.clear();
    rt.fill(NIGHT, NIGHT_ALPHA);
    // A gentle flicker on the lanterns' light.
    const flicker = 1 + 0.03 * Math.sin(now / 130);
    this.lanterns.forEach((l, i) => {
      const img = this.lights[i]!;
      img.setPosition((l.x + 0.5) * TILE_SIZE + MARGIN, (l.y + 0.5) * TILE_SIZE + MARGIN);
      img.setScale(l.lit ? LANTERN * flicker : EMBER);
    });
    this.headlamp.setPosition(px + MARGIN, py + MARGIN);
    rt.erase([...this.lights, this.headlamp]);
    rt.render();
    if (changed || now % 4 === 0) this.drawGlows(now);
  }

  private drawGlows(now: number): void {
    const g = this.glows.clear();
    const flick = 0.1 + 0.03 * Math.sin(now / 130);
    for (const l of this.lanterns) {
      if (!l.lit) continue;
      g.fillStyle(0xffc860, flick).fillCircle((l.x + 0.5) * TILE_SIZE, (l.y + 0.5) * TILE_SIZE, 30);
    }
  }
}
